import api from './api';

export interface AtsPlatform {
  code: string;
  name: string;
  logoUrl?: string;
  brandColor?: string;
  integratorCode: string;
  supportsJobImport: boolean;
  supportsJobExport: boolean;
  supportsCandidateImport: boolean;
  supportsCandidateExport: boolean;
  requiresJobForCandidateExport?: boolean;
}

export interface AtsConnection {
  id: string;
  name: string;
  status: 'pending' | 'connected' | 'disconnected' | 'error' | 'expired';
  lastSyncAt?: string;
  jobsImported: number;
  jobsUpdated: number;
  jobsExported: number;
  candidatesImported: number;
  candidatesUpdated: number;
  candidatesExported: number;
  createdAt: string;
  platform: {
    id: string;
    code: string;
    name: string;
    logoUrl?: string;
    brandColor?: string;
    supportsJobImport: boolean;
    supportsJobExport: boolean;
    supportsCandidateImport: boolean;
    supportsCandidateExport: boolean;
    requiresJobForCandidateExport?: boolean;
    integrator: {
      id: string;
      code: string;
      name: string;
    };
  };
}

export interface AtsSyncLog {
  id: string;
  connectionId: string;
  direction: 'import' | 'export';
  entityType: 'job' | 'candidate' | 'report';
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'partial';
  totalItems: number;
  processedItems: number;
  successItems: number;
  createdItems: number;
  updatedItems: number;
  failedItems: number;
  errors?: Array<{ itemId: string; error: string }>;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
}

export const atsService = {
  async getAvailablePlatforms(): Promise<AtsPlatform[]> {
    const response = await api.get('/ats/platforms');
    return response.data;
  },

  async getUserConnections(): Promise<AtsConnection[]> {
    const response = await api.get('/ats/connections');
    return response.data;
  },

  async getConnection(id: string): Promise<AtsConnection> {
    const response = await api.get(`/ats/connections/${id}`);
    return response.data;
  },

  async initiateConnection(data: {
    platformCode: string;
    name: string;
    integratorCode?: string;
  }): Promise<{ linkToken: string; magicLinkUrl: string; state: string }> {
    const response = await api.post('/ats/connections/initiate', data);
    return response.data;
  },

  async completeConnection(data: { publicToken: string; state: string }): Promise<AtsConnection> {
    const response = await api.post('/ats/connections/complete', data);
    return response.data;
  },

  async testConnection(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/ats/connections/${id}/test`);
    return response.data;
  },

  async deleteConnection(id: string): Promise<void> {
    await api.delete(`/ats/connections/${id}`);
  },

  async importJobs(connectionId: string, options?: { cursor?: string; limit?: number }): Promise<AtsSyncLog> {
    const response = await api.post('/ats/import/jobs', {
      connectionId,
      ...options,
    });
    return response.data;
  },

  async importCandidates(connectionId: string, options?: { cursor?: string; limit?: number }): Promise<AtsSyncLog> {
    const response = await api.post('/ats/import/candidates', {
      connectionId,
      ...options,
    });
    return response.data;
  },

  async exportJobs(connectionId: string, jobIds: string[]): Promise<AtsSyncLog> {
    const response = await api.post('/ats/export/jobs', {
      connectionId,
      jobIds,
    });
    return response.data;
  },

  async exportCandidates(
    connectionId: string,
    candidateIds: string[],
    externalJobId?: string
  ): Promise<AtsSyncLog> {
    const response = await api.post('/ats/export/candidates', {
      connectionId,
      candidateIds,
      externalJobId,
    });
    return response.data;
  },

  async getSyncLogs(connectionId?: string): Promise<AtsSyncLog[]> {
    const params = connectionId ? { connectionId } : {};
    const response = await api.get('/ats/sync-logs', { params });
    return response.data;
  },

  async getSyncLogStatus(syncLogId: string): Promise<AtsSyncLog> {
    const response = await api.get(`/ats/sync-logs/${syncLogId}`);
    return response.data;
  },

  async checkPendingConnectionStatus(state: string): Promise<{
    status: 'pending' | 'completed' | 'expired' | 'not_found';
    connectionId?: string;
    platformCode?: string;
  }> {
    const response = await api.get(`/ats/connections/pending/${state}`);
    return response.data;
  },
};

export default atsService;
